import { Component, Inject, OnInit, Optional } from '@angular/core';
import { DateRangeInterval } from '@app/core/components/date-range/date-range.component';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { DataTaskService } from '@app/core/services/data-task.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { AccountingAllocationItem, AccountingService } from '@app/services/accounting.service';
import { DepartmentItem, DepartmentService } from '@app/services/department.service';
import { EquipmentListItem, EquipmentService } from '@app/services/equipment.service';
import { LabourerItem, LabourService } from '@app/services/labour.service';
import { BusinesscoItem, LogisticService } from '@app/services/logistic.service';
import { ProjectItem, ProjectService } from '@app/services/project.service';

@Component({
    selector: 'tt-accounting-allocation-basis',
    templateUrl: './accounting-allocation-basis.component.html',
    styleUrls: ['./accounting-allocation-basis.component.css'],
})
export class AccountingAllocationBasisComponent implements OnInit {
    public get?: AccountingAllocationBasisGet;

    public companies: BusinesscoItem[] = [];

    public accountingAllocations: AccountingAllocationItem[] = [];

    public grid?: GridOptions;

    constructor(
        private datatask: DataTaskService,
        private logistic: LogisticService,
        private project: ProjectService,
        private department: DepartmentService,
        private equipment: EquipmentService,
        private labour: LabourService,
        private accounting: AccountingService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public searchAccounts = async ({ value }: { value: string | null }): Promise<any[]> => {
        return this.accounting.searchAccounts({ searchtext: value || '', businessco_no: this.get!.businessco_no });
    };

    public searchProjects = ({ value }: { value: string | null }): Promise<ProjectItem[]> => {
        return this.project.getProjects({ bg_projects_activestatus: 'ALL', businessco_no: this.get!.businessco_no, searchtext: value || '', argtype: '0', argvalue: '0' });
    };

    public searchDepartments = ({ value }: { value: string | null }): Promise<DepartmentItem[]> => {
        return this.department.getDepartments({ businessco_no: this.get!.businessco_no });
    };

    public searchEquipments = ({ value }: { value: string | null }): Promise<EquipmentListItem[]> => {
        return this.equipment.getEquipmentList({ businessco_no: this.get!.businessco_no });
    };

    public searchLabours = ({ value }: { value: string | null }): Promise<LabourerItem[]> => {
        return this.labour.getLabourers({ businessco_no: this.get!.businessco_no });
    };

    public onBusinesscoNoChanged() {
        if (!this.get) return;

        this.readGrid();
    }

    public readGrid() {
        if (!this.get) return;

        if (!this.grid) {
            this.grid = new Grid().setRememberId(this.get.grid_std_view_id).setLoadDataFunction({ method: +this.get.grid_std_p2_datatask_keyno, parameters: () => ({ ...this.get, account_nos: this.getIds(this.get?.account_nos), project_keynos: this.getIds(this.get?.projects), department_ids: this.getIds(this.get?.departments), equipment_ids: this.getIds(this.get?.equipments), labour_nos: this.getIds(this.get?.labours) }) });
        } else {
            this.grid.gridfunc?.read();
        }
    }

    private async loadGet() {
        this.get = await this.getAccountingAllocationBasis();
    }

    private async loadCompanies() {
        this.companies = await this.logistic.getCompanies();
    }

    private async loadAccountingAllocations() {
        if (!!this.get) {
            this.accountingAllocations = await this.accounting.searchAccountingAllocations({ searchtext: '', businessco_no: this.get.businessco_no });
        }
    }

    private async getAccountingAllocationBasis(): Promise<AccountingAllocationBasisGet> {
        const response = (await this.datatask.Post(4007))[0];

        return { ...response, account_nos: JSON.parse(response.account_nos), projects: JSON.parse(response.projects), departments: JSON.parse(response.departments), equipments: JSON.parse(response.equipments), labours: JSON.parse(response.labours) };
    }

    private getIds(array: unknown): string[] {
        let ids: string[] = [];

        if (!!array && Array.isArray(array) && array.every((item) => 'item_id' in item)) {
            ids = array.map((item) => item.item_id);
        }

        return ids;
    }

    private async init() {
        this.loadCompanies();
        await this.loadGet();
        this.loadAccountingAllocations();
        this.readGrid();
    }

    ngOnInit(): void {
        this.init();
    }
}

export interface AccountingAllocationBasisGet {
    businessco_no: string;
    show_details: '1' | '0';
    grid_std_p2_datatask_keyno: string;
    grid_std_view_id: string;
    accountingallocation_keyno: string;
    projects: ProjectItem[];
    account_nos: { item_id: number; item_name: string }[];
    departments: DepartmentItem[];
    equipments: EquipmentListItem[];
    labours: LabourerItem[];
    date_fom: string;
    date_tom: string;
    dateselector_index: DateRangeInterval;
    show_accountingallocation_keyno: '1' | '0';
    show_projects: '1' | '0';
    show_account_nos: '1' | '0';
    show_departments: '1' | '0';
    show_labours: '1' | '0';
    show_equipments: '1' | '0';
}
